/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, JSX } from 'preact';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { Grid } from '../UNSAFE_Grid';
import { classNames } from '../utils/UNSAFE_classNames';

type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
type Props = {
  children?: ComponentChildren;
  /**
   * The centermost element that should display more important data.
   */
  primary?: ComponentChildren;
  /**
   * Render Selector component in the leftmost column of the grid.
   */
  selector?: ComponentChildren;
  /**
   * Visual element to the left of the primary section.
   * Supports image, avatar, badge, initials, or icon.
   */
  leading?: ComponentChildren;
  /**
   * A section that is rendered above the primary line.
   */
  overline?: ComponentChildren;
  /**
   * A section below the primary line.
   */
  secondary?: ComponentChildren;
  /**
   * Another section below secondary.
   */
  tertiary?: ComponentChildren;
  /**
   * Provides information about other data.
   * Some examples of basic metadata are author, date created, date modified, file size, abbreviations, etc.
   */
  metadata?: ComponentChildren;
  /**
   * Visual element to the right of the primary section and metadata.
   * Example components are image, avatar, badge, initials, or icon.
   */
  trailing?: ComponentChildren;
  /**
   * The action prop displays either one primary action or one or more secondary actions.
   * A common example is using Toolbar.
   */
  action?: ComponentChildren;
  /**
   * Additional section separated with a space between itself and tertiary.
   */
  quaternary?: ComponentChildren;
  /**
   * Navigation should normally be used for linking away from the layout.
   */
  navigation?: ComponentChildren;
} & IntrinsicProps;

const childrenStyles = cssProps`${{
  /* @ts-ignore */
  '& > *': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '0 !important',
    marginRight: '0 !important'
  }
}}`;

// TODO: revisit idea of negative margin when selector has a proper API for handling spacing
// Jira: JET-54543
// negative margin added to selector
const selectorStyles = cssProps`${{
  alignSelf: 'center',
  margin: '-0.875rem -0.875rem'
}}`;

// most content will be vertically centered
const centerVerticalStyles = cssProps`${{
  alignSelf: 'center',
  minWidth: 0
}}`;

// extra spacing is needed between meta, trailing, and action components
const leftPaddingStyles = cssProps`${{
  paddingInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-4x)'
}}`;

// the central text area displays overline, primary, secondary, and tertiary in a column
const flexColumnStyles = cssProps`${{
  display: 'flex',
  flexDirection: 'column'
}}`;

// "extra" components in row 1 and the link in row 2 are justified on the righthand side of the list item layout
const flexRowStyles = cssProps`${{
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'right',
  alignSelf: 'center'
}}`;

const rightJustifyStyles = cssProps`${{
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'right'
}}`;

const rowAlignStyles = cssProps`${{
  alignSelf: 'end'
}}`;

export function ListItemLayout({
  children,
  selector,
  leading,
  primary,
  secondary,
  tertiary,
  overline,
  metadata,
  trailing,
  action,
  quaternary,
  navigation
}: Props) {
  // generate grid skeleton, adding cells based on which props are defined
  const { columnsTemplate, firstCell, secondCell, lastCell, hasSecondRow } = getColumnsTemplate(
    selector,
    leading,
    metadata,
    trailing,
    action,
    quaternary,
    navigation
  );

  // if ListItemLayout component has a child (ListItemTextLayout) then render that instead of individual props
  const textArea = children ? (
    <div className={childrenStyles}>{children}</div>
  ) : (
    <div className={flexColumnStyles}>
      <div>{overline}</div>
      <div>{primary}</div>
      <div>{secondary}</div>
      <div>{tertiary}</div>
    </div>
  );

  // a second row is needed when quaternary or navigation are defined
  const secondRow = hasSecondRow
    ? [
        firstCell && <span> </span>,
        secondCell && <span> </span>,
        <div className={rowAlignStyles}>{quaternary}</div>,
        lastCell && <div className={rightJustifyStyles}>{navigation}</div>
      ]
    : null;

  return (
    <Grid gap="4x" gridTemplateColumns={columnsTemplate}>
      {firstCell}
      {secondCell}
      <div className={centerVerticalStyles}>{textArea}</div>
      {lastCell}
      {secondRow}
    </Grid>
  );
}

function getColumnsTemplate(
  selector: ComponentChildren,
  leading: ComponentChildren,
  metadata: ComponentChildren,
  trailing: ComponentChildren,
  action: ComponentChildren,
  quaternary: ComponentChildren,
  navigation: ComponentChildren
) {
  const columnsTemplateBuilder = [];

  let firstCell = null;
  if (selector) {
    columnsTemplateBuilder.push('auto');
    firstCell = <div className={selectorStyles}>{selector}</div>;
  }

  let secondCell = null;
  if (leading) {
    columnsTemplateBuilder.push('auto');
    secondCell = <div className={centerVerticalStyles}>{leading}</div>;
  }

  columnsTemplateBuilder.push('1fr'); // Text area always gets 1 full fraction of allotted space

  let lastCell = null;
  if (metadata || trailing || action || navigation) {
    columnsTemplateBuilder.push('auto');
    lastCell = (
      <div className={flexRowStyles}>
        <div className={classNames([centerVerticalStyles, metadata ? leftPaddingStyles : ''])}>
          {metadata}
        </div>
        <div className={classNames([centerVerticalStyles, trailing ? leftPaddingStyles : ''])}>
          {trailing}
        </div>
        <div className={classNames([centerVerticalStyles, action ? leftPaddingStyles : ''])}>
          {action}
        </div>
      </div>
    );
  }

  const columnsTemplate = columnsTemplateBuilder.join(' ');
  const hasSecondRow = quaternary || navigation ? true : false;

  return { columnsTemplate, firstCell, secondCell, lastCell, hasSecondRow };
}
